// MCHD Room Scheduler - Assignment API Endpoints
// API client functions for assignment management operations

import { api } from '../index.js';
import type { ApiResponse } from '../index.js';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface Assignment {
  id: string;
  title: string;
  description?: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
  status: 'CONFIRMED' | 'CANCELLED';
  published: boolean;
  note?: string;
  createdAt: string;
  updatedAt: string;
  room: {
    id: string;
    number: string;
    name: string;
    capacity: number;
    roomNumber?: string; // Aliased field for frontend compatibility
  };
  users?: {
    id: string;
    name: string;
    email: string;
    specialty?: string;
    fullName?: string; // Aliased field for frontend compatibility
  };
  doctor?: {
    id: string;
    name: string;
    email: string;
    specialty?: string;
    fullName?: string; // Aliased field for frontend compatibility
  };
  sessionTemplate?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    label?: string; // Aliased field for frontend compatibility
    startMinutes?: string; // Aliased field for frontend compatibility
    endMinutes?: string; // Aliased field for frontend compatibility
  };
}

export interface AssignmentFilters {
  page?: number;
  limit?: number;
  search?: string;
  date?: string;
  roomId?: string;
  userId?: string;
  status?: string;
  published?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
}

export interface AssignmentStats {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  conflicts: number;
}

export interface ExportConfig {
  format: 'json' | 'csv' | 'xlsx' | 'pdf';
  filename: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  scope?: {
    includeAssignments?: boolean;
    includeBlocks?: boolean;
    includeConflicts?: boolean;
    includeUtilization?: boolean;
    includeMetadata?: boolean;
  };
}

export interface ExportResult {
  success: boolean;
  downloadUrl?: string;
  filename?: string;
  expiresAt?: string;
  format?: string;
  size?: number;
  error?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get assignments with filtering and pagination
 * @param filters - Query filters and pagination
 * @returns Promise with assignments data
 */
export async function getAssignments(filters: AssignmentFilters = {}): Promise<ApiResponse<{
  assignments: Assignment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}>> {
  const params = new URLSearchParams();
  
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.date) params.append('date', filters.date);
  if (filters.roomId) params.append('roomId', filters.roomId);
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.status) params.append('status', filters.status);
  if (filters.published !== undefined) params.append('published', filters.published.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  return api.get(`/assignments?${params.toString()}`);
}

/**
 * Get assignment statistics
 * @returns Promise with assignment statistics
 */
export async function getAssignmentStats(): Promise<ApiResponse<AssignmentStats>> {
  return api.get('/assignments/stats');
}

/**
 * Get assignment by ID
 * @param id - Assignment ID
 * @returns Promise with assignment data
 */
export async function getAssignmentById(id: string): Promise<ApiResponse<Assignment>> {
  return api.get(`/assignments/${id}`);
}

/**
 * Create a new assignment
 * @param assignmentData - Assignment data
 * @returns Promise with created assignment
 */
export async function createAssignment(assignmentData: Partial<Assignment>): Promise<ApiResponse<Assignment>> {
  return api.post('/assignments', assignmentData);
}

/**
 * Update an assignment
 * @param id - Assignment ID
 * @param assignmentData - Updated assignment data
 * @returns Promise with updated assignment
 */
export async function updateAssignment(id: string, assignmentData: Partial<Assignment>): Promise<ApiResponse<Assignment>> {
  return api.put(`/assignments/${id}`, assignmentData);
}

/**
 * Delete an assignment
 * @param id - Assignment ID
 * @returns Promise with deletion result
 */
export async function deleteAssignment(id: string): Promise<ApiResponse<void>> {
  return api.delete(`/assignments/${id}`);
}

/**
 * Export assignments data
 * @param config - Export configuration
 * @returns Promise with export result
 */
export async function exportAssignments(config: ExportConfig): Promise<ExportResult> {
  try {
    const response = await api.post('/assignments/export', config);
    
    if (response.success && response.data) {
      return {
        success: true,
        downloadUrl: response.data.downloadUrl,
        filename: response.data.filename,
        expiresAt: response.data.expiresAt,
        format: response.data.format,
        size: response.data.size
      };
    } else {
      return {
        success: false,
        error: response.error?.message || 'Export failed'
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Export failed'
    };
  }
}
